package com.daytimegaming.ourdailyfinances.presentation.home

import androidx.compose.foundation.background
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.tooling.preview.PreviewLightDark
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.daytimegaming.ourdailyfinances.domain.model.RecurringTransactionStream
import com.daytimegaming.ourdailyfinances.domain.model.Transaction
import com.daytimegaming.ourdailyfinances.presentation.preview.RecurringTransactionDetailPreviewParameterProvider
import com.daytimegaming.ourdailyfinances.presentation.theme.AppTheme
import com.daytimegaming.ourdailyfinances.presentation.theme.GlassCard
import com.daytimegaming.ourdailyfinances.presentation.util.formatAmount
import com.daytimegaming.ourdailyfinances.presentation.util.toCurrencySymbol
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

@Composable
fun RecurringTransactionDetailScreen(
    streamId: String,
    onNavigateBack: () -> Unit,
    onTransactionClick: (String, String?) -> Unit,
    viewModel: RecurringTransactionDetailViewModel = koinViewModel(key = streamId) { parametersOf(streamId) }
) {
    val state by viewModel.state.collectAsStateWithLifecycle()

    RecurringTransactionDetailScreenContent(
        state = state,
        onNavigateBack = onNavigateBack,
        onTransactionClick = onTransactionClick
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun RecurringTransactionDetailScreenContent(
    state: RecurringTransactionDetailScreenState,
    onNavigateBack: () -> Unit,
    onTransactionClick: (String, String?) -> Unit
) {
    Scaffold(
        modifier = Modifier.fillMaxSize(),
        topBar = {
            TopAppBar(
                title = {
                    Text(
                        text = when (state) {
                            is RecurringTransactionDetailScreenState.Loaded -> {
                                val stream = state.stream
                                if (stream.merchantName.isNullOrBlank()) stream.description else stream.merchantName
                            }
                            else -> "Subscription Details"
                        },
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold
                    )
                },
                navigationIcon = {
                    IconButton(onClick = onNavigateBack) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = "Back"
                        )
                    }
                }
            )
        }
    ) { contentPadding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(contentPadding)
        ) {
            when (state) {
                is RecurringTransactionDetailScreenState.Loading -> {
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator()
                    }
                }
                is RecurringTransactionDetailScreenState.Error -> {
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = state.message,
                            color = MaterialTheme.colorScheme.error,
                            style = MaterialTheme.typography.bodyMedium
                        )
                    }
                }
                is RecurringTransactionDetailScreenState.Loaded -> {
                    RecurringTransactionDetailContent(
                        stream = state.stream,
                        transactions = state.transactions,
                        transactionDashboardIds = state.transactionDashboardIds,
                        onTransactionClick = onTransactionClick
                    )
                }
            }
        }
    }
}

@Composable
private fun RecurringTransactionDetailContent(
    stream: RecurringTransactionStream,
    transactions: List<Transaction>,
    transactionDashboardIds: Map<String, String?>,
    onTransactionClick: (String, String?) -> Unit
) {
    val currencySymbol = stream.averageAmount.isoCurrencyCode.toCurrencySymbol()
    val isInflow = stream.streamType == "inflow"
    val absAmount = kotlin.math.abs(stream.averageAmount.amount)
    val formattedAmount = absAmount.formatAmount()
    val parts = formattedAmount.split('.')
    val intPart = parts.getOrElse(0) { "0" }
    val decPart = parts.getOrNull(1) ?: "00"

    LazyColumn(
        modifier = Modifier.fillMaxSize(),
        contentPadding = PaddingValues(16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        // Large Amount Display
        item {
            Column(
                modifier = Modifier.fillMaxWidth(),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Text(
                    text = if (isInflow) "RECURRING INCOME (MONEY IN)" else "RECURRING BILL / SUB (MONEY OUT)",
                    style = MaterialTheme.typography.labelSmall.copy(letterSpacing = 2.sp),
                    color = if (isInflow) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurfaceVariant,
                    fontWeight = FontWeight.Bold
                )
                Spacer(modifier = Modifier.height(8.dp))
                Row(
                    verticalAlignment = Alignment.Bottom
                ) {
                    Text(
                        text = if (isInflow) "+" else "-",
                        style = MaterialTheme.typography.titleLarge,
                        color = if (isInflow) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.error,
                        modifier = Modifier.padding(bottom = 12.dp)
                    )
                    Text(
                        text = currencySymbol,
                        style = MaterialTheme.typography.titleLarge,
                        color = MaterialTheme.colorScheme.primary,
                        modifier = Modifier.padding(bottom = 12.dp)
                    )
                    Text(
                        text = intPart,
                        style = MaterialTheme.typography.displayLarge.copy(fontSize = 48.sp),
                        color = MaterialTheme.colorScheme.onSurface,
                        fontWeight = FontWeight.Bold
                    )
                    Text(
                        text = ".$decPart",
                        style = MaterialTheme.typography.titleLarge,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        modifier = Modifier.padding(bottom = 8.dp)
                    )
                }
                Spacer(modifier = Modifier.height(12.dp))
                // Status badges
                Row(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Box(
                        modifier = Modifier
                            .background(
                                color = if (stream.isActive) {
                                    MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.2f)
                                } else {
                                    MaterialTheme.colorScheme.errorContainer.copy(alpha = 0.2f)
                                },
                                shape = RoundedCornerShape(999.dp)
                            )
                            .padding(horizontal = 12.dp, vertical = 6.dp)
                    ) {
                        Text(
                            text = if (stream.isActive) "ACTIVE" else "INACTIVE",
                            style = MaterialTheme.typography.labelMedium,
                            color = if (stream.isActive) {
                                MaterialTheme.colorScheme.primary
                            } else {
                                MaterialTheme.colorScheme.error
                            },
                            fontWeight = FontWeight.Bold
                        )
                    }

                    Box(
                        modifier = Modifier
                            .background(
                                color = Color(0x1FBEC6E0),
                                shape = RoundedCornerShape(999.dp)
                            )
                            .padding(horizontal = 12.dp, vertical = 6.dp)
                    ) {
                        Text(
                            text = stream.status.uppercase(),
                            style = MaterialTheme.typography.labelMedium,
                            color = MaterialTheme.colorScheme.secondary,
                            fontWeight = FontWeight.Bold
                        )
                    }
                }
                Spacer(modifier = Modifier.height(8.dp))
            }
        }

        // Basic Information Card
        item {
            CardSection(title = "Subscription Details") {
                DetailRow(label = "Description", value = stream.description)
                stream.merchantName?.let {
                    DetailRow(label = "Merchant Name", value = it)
                }
                DetailRow(label = "Frequency", value = stream.frequency.lowercase().replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() })
                DetailRow(label = "Stream ID", value = stream.streamId)
                DetailRow(label = "Account ID", value = stream.accountId)
            }
        }

        // Amounts Card
        item {
            CardSection(title = "Amount Analysis") {
                val lastSymbol = stream.lastAmount.isoCurrencyCode.toCurrencySymbol()
                val avgSign = if (isInflow) "+" else "-"
                DetailRow(label = "Average Amount", value = "$avgSign$currencySymbol${stream.averageAmount.amount.formatAmount()}")
                DetailRow(label = "Last Amount", value = "$avgSign$lastSymbol${stream.lastAmount.amount.formatAmount()}")
            }
        }

        // Dates Timeline Card
        item {
            CardSection(title = "Timeline") {
                DetailRow(label = "First Detected Date", value = stream.firstDate)
                DetailRow(label = "Last Detected Date", value = stream.lastDate)
                DetailRow(label = "Predicted Next Date", value = stream.predictedNextDate ?: "N/A")
            }
        }

        // Associated Transactions Section
        if (transactions.isNotEmpty()) {
            item {
                Text(
                    text = "Associated Transactions",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface,
                    modifier = Modifier.padding(start = 4.dp, bottom = 4.dp)
                )
            }
            items(transactions, key = { it.transactionId }) { tx ->
                val dashboardId = transactionDashboardIds[tx.transactionId]
                GlassCard(
                    modifier = Modifier.fillMaxWidth(),
                    onClick = { onTransactionClick(tx.transactionId, dashboardId) }
                ) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Column(
                            modifier = Modifier.weight(1.3f)
                        ) {
                            Text(
                                text = tx.merchantName ?: tx.name,
                                style = MaterialTheme.typography.bodyLarge,
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.onSurface
                            )
                            Spacer(modifier = Modifier.height(4.dp))
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                Box(
                                    modifier = Modifier
                                        .background(Color(0x1FBEC6E0), shape = RoundedCornerShape(999.dp))
                                        .padding(horizontal = 8.dp, vertical = 2.dp)
                                ) {
                                    Text(
                                        text = tx.category.firstOrNull()?.replace("_", " ") ?: "",
                                        style = MaterialTheme.typography.labelSmall,
                                        color = MaterialTheme.colorScheme.secondary
                                    )
                                }
                                if (tx.pending) {
                                    Text(
                                        text = "Pending",
                                        style = MaterialTheme.typography.labelSmall,
                                        color = MaterialTheme.colorScheme.error,
                                        modifier = Modifier
                                            .clip(RoundedCornerShape(4.dp))
                                            .background(MaterialTheme.colorScheme.error.copy(alpha = 0.1f))
                                            .padding(horizontal = 8.dp, vertical = 2.dp)
                                    )
                                }
                            }
                        }
                        val isExpense = tx.amount > 0
                        val formattedAmount = if (isExpense) {
                            "-$${tx.amount.formatAmount()}"
                        } else {
                            "+$${kotlin.math.abs(tx.amount).formatAmount()}"
                        }
                        val color = if (isExpense) MaterialTheme.colorScheme.onSurface else MaterialTheme.colorScheme.primary
                        Column(
                            horizontalAlignment = Alignment.End
                        ) {
                            Text(
                                text = formattedAmount,
                                style = MaterialTheme.typography.bodyLarge,
                                fontWeight = FontWeight.Bold,
                                color = color
                            )
                            Spacer(modifier = Modifier.height(4.dp))
                            Text(
                                text = tx.date,
                                style = MaterialTheme.typography.labelSmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                }
            }
        } else if (stream.transactionIds.isNotEmpty()) {
            item {
                CardSection(title = "Associated Transaction IDs") {
                    stream.transactionIds.forEachIndexed { index, txId ->
                        DetailRow(label = "Transaction #${index + 1}", value = txId)
                    }
                }
            }
        }

        // Category Card
        stream.personalFinanceCategory?.let { pfc ->
            item {
                CardSection(title = "Categories") {
                    DetailRow(label = "Primary Category", value = pfc.primary.replace("_", " "))
                    DetailRow(label = "Detailed Category", value = pfc.detailed.replace("_", " "))
                    pfc.confidenceLevel?.let {
                        DetailRow(label = "Confidence Level", value = it)
                    }
                    pfc.version?.let {
                        DetailRow(label = "Category Version", value = it)
                    }
                }
            }
        }
    }
}

@Composable
private fun CardSection(
    title: String,
    content: @Composable ColumnScope.() -> Unit
) {
    Column(modifier = Modifier.fillMaxWidth()) {
        Text(
            text = title,
            style = MaterialTheme.typography.titleMedium,
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.onSurface,
            modifier = Modifier.padding(start = 4.dp, bottom = 8.dp)
        )
        GlassCard(modifier = Modifier.fillMaxWidth()) {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                ColumnScope().content()
            }
        }
    }
}

private class ColumnScope

@Composable
private fun DetailRow(
    label: String,
    value: String
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.SpaceBetween,
    ) {
        Text(
            text = label,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        Text(
            text = value,
            style = MaterialTheme.typography.bodyMedium,
            fontWeight = FontWeight.SemiBold,
            color = MaterialTheme.colorScheme.onSurface
        )
    }
}

@PreviewLightDark
@Composable
fun RecurringTransactionDetailScreenPreview(
    @PreviewParameter(RecurringTransactionDetailPreviewParameterProvider::class) state: RecurringTransactionDetailScreenState
) {
    AppTheme(
        darkTheme = isSystemInDarkTheme(),
        dynamicColor = false,
    ) {
        Surface {
            RecurringTransactionDetailScreenContent(
                state = state,
                onNavigateBack = {},
                onTransactionClick = { _, _ -> }
            )
        }
    }
}
